import React, { useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  Store as StoreIcon,
  Users,
  LogOut,
  ChevronDown,
  ChevronRight,
  Menu,
  AlertTriangle,
  RefreshCw,
  SearchCheck,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import LanguageSwitcher from "./LanguageSwitcher";

const buildNav = (t, isAdmin, canCreateStore) => [
  {
    key: "dashboard",
    label: t("sidebar.dashboard"),
    to: "/dashboard",
    icon: LayoutDashboard,
    show: true,
  },
  {
    key: "stores",
    label: t("sidebar.stores"),
    icon: StoreIcon,
    show: true,
    children: [
      { label: t("sidebar.allStores"), to: "/stores", show: true },
      { label: t("sidebar.addStore"), to: "/stores/new", show: canCreateStore },
      { label: t("sidebar.storeClosedDays"), to: "/store-closed-days", show: isAdmin },
    ],
  },
  {
    key: "missingSales",
    label: t("sidebar.missingSales"),
    icon: SearchCheck,
    show: true,
    children: [
      { label: t("sidebar.missingSalesRadar"), to: "/missing-sales/radar", show: true },
      { label: t("sidebar.missingSalesLog"), to: "/missing-sales/log", show: true },
    ],
  },
  {
    key: "converters",
    label: t("sidebar.converters"),
    icon: RefreshCw,
    show: true,
    children: [
      { label: t("sidebar.allConverters"), to: "/converters", show: true },
      { label: t("sidebar.addConverter"), to: "/converters/new", show: canCreateStore },
    ],
  },
  {
    key: "errors",
    label: t("sidebar.errors"),
    to: "/errors",
    icon: AlertTriangle,
    show: true,
  },
  {
    key: "settings",
    label: t("sidebar.settings"),
    icon: Users,
    show: isAdmin,
    children: [
      { label: t("sidebar.users"), to: "/users", show: isAdmin },
      { label: t("sidebar.emailConfig"), to: "/email-config", show: isAdmin },
    ],
  },
];

export default function Layout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState({
    stores: true,
    missingSales: true,
    converters: true,
    settings: true,
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = user?.group === "admin";
  const canCreateStore = isAdmin; // only admin sees "Add new store"

  const visibleItems = useMemo(() => {
    return buildNav(t, isAdmin, canCreateStore)
      .filter((i) => i.show)
      .map((item) => ({
        ...item,
        visibleChildren: item.children ? item.children.filter((c) => c.show) : null,
      }));
  }, [isAdmin, canCreateStore, t]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const groupLabel = user?.group ? t(`groups.${user.group}`, { defaultValue: user.group }) : "";

  return (
    <div className="min-h-screen bg-[#f0f4f9]">
      {/* Sidebar — fixed on desktop, slide-in on mobile */}
      <aside
        className={`pg-sidebar fixed z-30 top-0 left-0 h-screen w-72 flex flex-col transform transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        <div className="px-5 py-5 border-b border-white/10 flex items-center gap-3 shrink-0">
          <img
            src="/assets/logo-icon.png"
            alt="Auto-on Solutions"
            className="w-14 h-14 logo-glow rounded-md"
          />
          <div>
            <div className="text-[11px] tracking-[0.32em] text-cyan-300/90 uppercase">
              POS · GTO
            </div>
            <div className="text-lg font-extrabold leading-none text-white">
              POS<span className="text-amber-400">2</span>GTO
            </div>
          </div>
        </div>

        <nav className="px-3 py-4 space-y-1 flex-1 overflow-y-auto">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            if (item.visibleChildren) {
              const isOpen = open[item.key];
              return (
                <div key={item.key}>
                  <button
                    onClick={() => setOpen((o) => ({ ...o, [item.key]: !o[item.key] }))}
                    aria-expanded={Boolean(isOpen)}
                    className="pg-sidebar-item w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium"
                    data-testid={`sidebar-${item.key}-toggle`}
                  >
                    <span className="flex items-center gap-3">
                      <Icon size={17} />
                      {item.label}
                    </span>
                    {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                  </button>
                  {isOpen && (
                    <div className="mt-1 ml-8 space-y-1">
                      {item.visibleChildren.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          end
                          className={({ isActive }) =>
                            `pg-sidebar-subitem block px-3 py-2 rounded-md text-[13px] ${
                              isActive ? "active" : ""
                            }`
                          }
                          data-testid={`sidebar-link-${c.to.replace(/\//g, "-")}`}
                          onClick={() => setMobileOpen(false)}
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={item.key}
                to={item.to}
                className={({ isActive }) =>
                  `pg-sidebar-item flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                    isActive ? "active" : ""
                  }`
                }
                data-testid={`sidebar-link-${item.key}`}
                onClick={() => setMobileOpen(false)}
              >
                <Icon size={17} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-5 py-4 border-t border-white/10 shrink-0">
          <div className="text-[11px] text-white/40 uppercase tracking-widest mb-2">
            {t("sidebar.poweredBy")}
          </div>
          <img
            src="/assets/logo-light.png"
            alt="Auto-on Solutions"
            className="h-8 object-contain"
          />
        </div>
      </aside>

      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-20 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main — offset by sidebar width on desktop */}
      <div className="lg:pl-72 min-h-screen flex flex-col">
        <header className="pg-topbar h-14 px-4 lg:px-8 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden text-white"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30">
              <span className="online-dot" />
              <span className="text-xs font-semibold text-emerald-300 tracking-wide uppercase">
                {t("common.online")}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="topbar" />
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-white leading-none">
                {user?.name || user?.username}
              </div>
              <div className="text-[11px] uppercase tracking-widest text-cyan-300/80 mt-1">
                {groupLabel}
              </div>
            </div>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 grid place-items-center text-white font-bold">
              {(user?.name || user?.username || "?").substring(0, 1).toUpperCase()}
            </div>
            <button
              onClick={handleLogout}
              className="text-white/80 hover:text-white p-2 rounded-md hover:bg-white/10 transition"
              data-testid="logout-btn"
              title={t("common.logout")}
              aria-label={t("common.logout")}
            >
              <LogOut size={17} />
            </button>
          </div>
        </header>

        <main className="p-4 lg:p-8 max-w-[1500px] w-full mx-auto flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
